import axios from "axios"
import { sockets, ioInit } from '@/plugins/banc.socketio.plugin'
import { connect, utils } from "near-api-js"
import { avatars } from '@/utilities'

const {
    VITE_APP_SERVER: API_SERVER,
    VITE_APP_NEAR_CONTRACT_ID,
    VITE_APP_NEAR_NETWORK_ID,
    VITE_APP_NEAR_RPC_URL,
    VITE_APP_ANAME_PUBLIC_KEY,
    MODE
} = import.meta.env

const axiosInstance = axios.create({
    headers: {
        "x-application": "june07-blur",
    },
    withCredentials: true
})

const apiService = (app, router) => {
    const request = async (passedOptions) => {
        const defaultOptions = {
            method: 'GET'
        }
        const options = {
            ...defaultOptions,
            ...passedOptions
        }

        try {
            const response = await axiosInstance(options)
            return options.returnResponse ? response : response.data
        } catch (error) {
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.log(error.response.data)
                console.log(error.response.status)
                console.log(error.response.headers)
            } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                console.log(error.request)
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log('Error', error.message)
            }
            console.log(error.config)
            throw error
        }
    }
    const authRequest = (options) => request({
        ...options,
        headers: {
            ...options.headers,
            'Authorization': `Bearer ${options.token}`
        }
    })
    const wsEmit = (options) => {
        return new Promise(async resolve => {
            const { eventName, auth, ...args } = options
            const socketIndex = auth ? 1 : 0

            try {
                if (auth && !sockets?.[1]) {
                    await ioInit({ auth })
                }

                sockets?.[socketIndex]?.emit(eventName, {
                    ...args
                }, (json) => {
                    const { error } = json

                    if (error) {
                        console.error(error)
                    }

                    resolve(json)
                })
            } catch (error) {
                console.error(error)
                resolve({ error })
            }
        })
    }

    function openIndexedDB(version = 5) {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open("BANC", version)
            request.onupgradeneeded = (event) => {
                const db = event.target.result

                    ;['votes', 'deposits', 'proofs', 'bookmarks', 'avatars', 'stats'].forEach((storeName) => {
                        if (!db.objectStoreNames.contains(storeName)) {
                            db.createObjectStore(storeName, { keyPath: 'id' })
                        }
                    })

                console.log('Database upgraded to version', version)
            }
            request.onsuccess = (event) => resolve(event.target.result)
            request.onerror = (event) => reject(event.target.error)
        })
    }

    async function getLastSavedProof(minTimestamp) {
        const db = await openIndexedDB()

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['proofs'], 'readonly')
            const objectStore = transaction.objectStore('proofs')
            const request = objectStore.openKeyCursor(null, 'prev') // Opens cursor in descending order

            request.onsuccess = async (event) => {
                const cursor = event.target.result

                if (cursor?.key >= minTimestamp) {
                    // Fetch the corresponding data only when needed
                    const proof = await getDataFromIndexedDB(cursor.key, 'proofs')
                    resolve(proof)
                } else {
                    resolve() // No recent proof found
                }
            }

            request.onerror = (event) => reject(event.target.error)
        })
    }

    // Function to get the data from IndexedDB
    async function getDataFromIndexedDB(id, storeName) {
        const db = await openIndexedDB()

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([storeName], 'readonly')
            const objectStore = transaction.objectStore(storeName)
            const request = objectStore.get(id)

            request.onsuccess = (event) => {
                resolve(event.target.result ? event.target.result.data : undefined)
            }
            request.onerror = (event) => reject(event.target.error)
        })
    }

    async function checkIfDataExists(id, storeName) {
        const db = await openIndexedDB()

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([storeName], 'readonly')
            const objectStore = transaction.objectStore(storeName)
            const request = objectStore.getKey(id)

            request.onsuccess = (event) => resolve(!!event.target.result)
            request.onerror = (event) => reject(event.target.error)
        })
    }

    async function executeIndexedDBOperation(storeName, callback) {
        const db = await openIndexedDB()
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([storeName], 'readwrite')
            const objectStore = transaction.objectStore(storeName)
            const request = callback(objectStore)

            request.onsuccess = () => resolve()
            request.onerror = (event) => reject(event.target.error)
        })
    }

    async function saveDataToIndexedDB(id, storeName, data) {
        await executeIndexedDBOperation(storeName, (objectStore) =>
            objectStore.put({ id, data })
        )
        app.config.globalProperties.$emitter.emit(`idb:updated:${storeName}`, { id, data })
    }

    async function deleteDataFromIndexedDB(id, storeName) {
        await executeIndexedDBOperation(storeName, (objectStore) =>
            objectStore.delete(id)
        )
        app.config.globalProperties.$emitter.emit(`idb:deleted:${storeName}`, { id })
    }
    async function withCreditCheck(args, callback) {
        const { store, auth } = args

        if (!store.banc) {
            const { credit } = await checkCredit({ auth })

            store.banc = credit
        }

        if (!store.banc) {
            router.push({ query: { ...router.currentRoute.value.query, outOfBanc: 1 } })
            throw (new Error('Out of BANC'))
        }

        return callback()
    }
    function dashSegmentMapHack(arr) {
        arr
            .flatMap(deposit => deposit.fuel)
            .filter(fuel => fuel.segments)
            .forEach(fuel =>
                fuel.segments.forEach(segment => {
                    window._segmentMap[segment.key] = segment.presignedURL
                })
            )
    }
    const checkCredit = ({ auth }) => wsEmit({ eventName: 'checkCredit', auth })

    return {
        request,
        authRequest,
        checkIfDataExists,
        getDataFromIndexedDB,
        saveDataToIndexedDB,
        getLastSavedProof,
        buildInfo: async () => {
            if (MODE !== 'production' && !/dev\./.test(window.location.host)) {
                return
            }
            try {
                const { data } = await axiosInstance.get(`${window.origin}/build-info.json`, {
                    headers: {
                        'Cache-Control': 'no-store, max-age=0',
                    },
                })
                return data
            } catch (error) {
                console.error(error)
            }
        },
        info: () => request({ url: `${API_SERVER}/v1/info` }),
        forumAuth: (token) => authRequest({ token, url: `${API_SERVER}/v1/banc/forum/auth` }),
        assignBancUsername: (token) => authRequest({ token, url: `${API_SERVER}/v1/banc/aname/${VITE_APP_ANAME_PUBLIC_KEY}` }),
        fetch: async (args = {}) => {
            const { filter } = args
            const { usersDeposits, error } = await wsEmit({ eventName: 'fetch', filter })

            if (error) {
                console.error(error)
                return
            }

            const usersDepositsWithAvatars = await Promise.all(usersDeposits.map(ud => avatars({ hash: ud.emailHash }).then(avatars => ({ ...ud, avatars }))))
            usersDepositsWithAvatars.forEach(async userDeposits => {
                const { userId, avatars, deposits } = userDeposits

                dashSegmentMapHack(deposits)

                saveDataToIndexedDB(userId, 'avatars', avatars)
                saveDataToIndexedDB(userId, 'deposits', deposits)
            })



            return usersDepositsWithAvatars
        },
        bookmark: async ({ auth, bookmark, value }) => {
            if (value) {
                saveDataToIndexedDB(bookmark, 'bookmarks', { updated: Date.now() })
            } else {
                deleteDataFromIndexedDB(bookmark, 'bookmarks')
            }

            try {
                await wsEmit({
                    eventName: 'updateStat', id: bookmark, value: value.isSet ? 0 : 1, auth
                })
            } catch (error) {
                console.error(error)
            }
        },
        updateStat: async ({ auth, id, value }) => {
            try {
                const newValue = /video|image|share/i.test(id) ? 1 : value.isSet ? 0 : 1
                await wsEmit({ eventName: 'updateStat', id, value: newValue, auth })
            } catch (error) {
                console.error(error)
            }
        },
        profile: async ({ userId }) => {
            const { profile, error } = await wsEmit({ eventName: 'profile', userId })

            if (error) {
                console.error(error)
                return
            }

            return profile
        },
        deposits: async ({ auth }) => {
            const userId = auth.sub
            const idbArr = await getDataFromIndexedDB(userId, 'deposits')
            const depositsMap = new Map(idbArr?.map(deposit => [deposit.timestamp, deposit]))
            let limit = { offset: 0, count: 10 }

            while (true) {
                const { user, error } = await wsEmit({ eventName: 'deposits', limit, auth })

                if (error) {
                    console.error(error)
                    return
                }

                if (user.deposits?.length <= 0) {
                    break
                }

                user.deposits.forEach(userDeposit => {
                    depositsMap.set(userDeposit.timestamp, userDeposit)
                })

                limit.offset += limit.count
            }

            const arr = Array.from(depositsMap.values())

            dashSegmentMapHack(arr)

            saveDataToIndexedDB(userId, 'deposits', arr)

            arr.sort((a, b) => b.timestamp - a.timestamp)

            return arr
        },
        updateCredit: async (args) => {
            const { store, auth } = args
            const { credit } = await checkCredit({ auth })

            store.banc = credit
        },
        recordVote: (args) => withCreditCheck(args, async () => {
            const { auth, unconfirmedVote } = args
            const { confirmedVote } = await wsEmit({ eventName: 'recordVote', unconfirmedVote, auth })

            if (confirmedVote) {
                await saveDataToIndexedDB(confirmedVote.userIdToS3Hash, 'votes', confirmedVote.choice)
            }
        }),
        getVote: (args) => wsEmit({ eventName: 'getVote', ...args }),
        getStats: ({ auth }) => wsEmit({ eventName: 'getStats', auth }),
        getDepositStats: (args) => wsEmit({ eventName: 'getDepositStats', ...args }),
        authCheck: (args) => wsEmit({ eventName: 'authCheck', ...args }),
        authHook: ({ auth }) => wsEmit({ eventName: 'authHook', auth }),
        confirmTransfer: (args) => wsEmit({ eventName: 'confirmTransfer', ...args }),
        submitWeight: (args) => wsEmit({ eventName: 'submitWeight', ...args }),
        checkCredit,
        web3: {
            transferBanc: async (wallet, amount, store) => {
                const callbackUrl = new URL(`${window.location.origin}${window.location.pathname}?transferredBanc=1`)

                try {
                    const result = await wallet.signAndSendTransaction({
                        receiverId: VITE_APP_NEAR_CONTRACT_ID,
                        actions: [
                            {
                                type: "FunctionCall",
                                params: {
                                    methodName: "ft_transfer",
                                    args: {
                                        receiver_id: VITE_APP_NEAR_CONTRACT_ID,
                                        amount: utils.format.parseNearAmount(`${amount}`)
                                    },
                                    gas: "20000000000000",
                                    deposit: "1"
                                }
                            }
                        ],
                        callbackUrl: callbackUrl.toString(),
                    })
                    store.web3TransactionHashes = Array.from(new Set([
                        ...store.web3TransactionHashes,
                        result.transaction.hash
                    ]))
                    router.push({ path: callbackUrl.pathname, query: Object.fromEntries(callbackUrl.searchParams) })
                } catch (error) {
                    console.error("Error querying FT transfers:", error)
                }
            },
            queryFTTransfers: async function (accountId, memo) {
                const near = await connect({
                    networkId: VITE_APP_NEAR_NETWORK_ID,
                    nodeUrl: VITE_APP_NEAR_RPC_URL
                })

                const account = await near.account(accountId) // Replace with your NEAR account

                try {
                    const result = await account.viewFunction(
                        VITE_APP_NEAR_CONTRACT_ID,  // FT contract
                        "get_transfers",  // Function to fetch past transfers (depends on contract implementation)
                        {
                            memo
                        }
                    )

                    return result
                } catch (error) {
                    console.error("Error querying FT transfers:", error)
                }
            }
        }
    }
}

export default {
    async install(app) {
        app.config.globalProperties.$api = apiService(app, app.config.globalProperties.$router)
    }
}